export type PermissionAccessNode = string | PermissionAccessExpression;

export interface PermissionAccessExpression {
  anyOf?: PermissionAccessNode[];
  allOf?: PermissionAccessNode[];
  noneOf?: PermissionAccessNode[];
  not?: PermissionAccessNode;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function normalizeAccessNodeList(value: unknown): PermissionAccessNode[] | null {
  if (!Array.isArray(value)) {
    return null;
  }

  const nodes = value
    .map((entry) => normalizePermissionAccess(entry))
    .filter((entry): entry is PermissionAccessNode => entry !== null);
  return nodes;
}

export function normalizePermissionAccess(value: unknown): PermissionAccessNode | null {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }

  if (Array.isArray(value)) {
    const nodes = normalizeAccessNodeList(value);
    if (nodes === null || nodes.length === 0) {
      return null;
    }
    return { anyOf: nodes };
  }

  if (!isRecord(value)) {
    return null;
  }

  const anyOf = normalizeAccessNodeList(value['anyOf']);
  const allOf = normalizeAccessNodeList(value['allOf']);
  const noneOf = normalizeAccessNodeList(value['noneOf']);
  const not = normalizePermissionAccess(value['not']);

  if (anyOf === null && allOf === null && noneOf === null && not === null) {
    return null;
  }

  return {
    ...(anyOf !== null ? { anyOf } : {}),
    ...(allOf !== null ? { allOf } : {}),
    ...(noneOf !== null ? { noneOf } : {}),
    ...(not !== null ? { not } : {}),
  };
}

export function evaluatePermissionAccess(
  node: PermissionAccessNode,
  effectivePermissions: Set<string>
): boolean {
  if (typeof node === 'string') {
    return effectivePermissions.has(node);
  }

  const checks: boolean[] = [];

  if (node.allOf !== undefined) {
    checks.push(node.allOf.every((entry) => evaluatePermissionAccess(entry, effectivePermissions)));
  }
  if (node.anyOf !== undefined) {
    checks.push(node.anyOf.some((entry) => evaluatePermissionAccess(entry, effectivePermissions)));
  }
  if (node.noneOf !== undefined) {
    checks.push(node.noneOf.every((entry) => !evaluatePermissionAccess(entry, effectivePermissions)));
  }
  if (node.not !== undefined) {
    checks.push(!evaluatePermissionAccess(node.not, effectivePermissions));
  }

  if (checks.length === 0) {
    return false;
  }

  return checks.every(Boolean);
}
